// Frontend helpers for the OAIP-62 beta gate. The actual enforcement lives
// in the Cloud Function (services/beta_gate.py); these helpers make the React
// app reflect gate state in the UI and thread the password through subsequent
// chat requests.

export type BetaRoute = "agency" | "vendor";

const PASSWORD_HEADER = "X-Apex-Beta-Password";

const devFunctionsUrl = "http://127.0.0.1:5001/dit-oaip-poc-ai-projects/us-east1";
const baseUrl =
  import.meta.env.VITE_FUNCTIONS_BASE_URL || (import.meta.env.DEV ? devFunctionsUrl : "/api");

function sessionTokenKey(route: BetaRoute): string {
  return `apex_${route}_beta_password`;
}

export function getStoredPassword(route: BetaRoute): string | null {
  try {
    return sessionStorage.getItem(sessionTokenKey(route));
  } catch {
    // sessionStorage can throw in privacy-mode or sandboxed iframes — treat as no token.
    return null;
  }
}

export function setStoredPassword(route: BetaRoute, value: string): void {
  try {
    sessionStorage.setItem(sessionTokenKey(route), value);
  } catch {
    // Best-effort; if storage fails the user will just be re-prompted next page.
  }
}

export function getBetaPasswordHeader(route: BetaRoute): Record<string, string> {
  const value = getStoredPassword(route);
  return value ? { [PASSWORD_HEADER]: value } : {};
}

export interface GateCheckResult {
  ipAllowed: boolean;
}

/** GET probe — does the caller's IP pass the allowlist? */
export async function checkGate(route: BetaRoute): Promise<GateCheckResult> {
  try {
    const response = await fetch(
      `${baseUrl}/apexBetaGateHandler?route=${encodeURIComponent(route)}`,
      { method: "GET" },
    );
    if (response.status === 403) return { ipAllowed: false };
    if (!response.ok) {
      // Treat any non-2xx (other than 403 IP-blocked) as blocked from the UI's
      // perspective — better to render nothing than to flash the chatbot in
      // front of someone who can't actually use it.
      return { ipAllowed: false };
    }
    const data = (await response.json().catch(() => ({}))) as Partial<GateCheckResult>;
    return { ipAllowed: data.ipAllowed === true };
  } catch {
    return { ipAllowed: false };
  }
}

/** POST a candidate password — true on success, false on 403, throws on misconfigured. */
export async function submitPassword(route: BetaRoute, password: string): Promise<boolean> {
  const response = await fetch(
    `${baseUrl}/apexBetaGateHandler?route=${encodeURIComponent(route)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    },
  );
  if (response.status === 200) {
    setStoredPassword(route, password);
    return true;
  }
  if (response.status === 503) {
    throw new Error("Beta password is not configured yet — contact the OAIP team.");
  }
  return false;
}
